import { Helmet } from "react-helmet-async";
import { useLocation } from "wouter";

interface SEOProps {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  image?: string | null;
  url?: string | null;
  type?: string | null;
  canonical?: string | null;
  noindex?: boolean;
}

export function SEO({ 
  title: propTitle,
  description: propDescription,
  keywords: propKeywords,
  image: propImage, 
  url,
  type: propType,
  canonical,
  noindex = false
}: SEOProps) {
  const [location] = useLocation();
  const title = propTitle || "AccelerateByte | Elite Tech Studio & Premium Software Development";
  const description = propDescription || "AccelerateByte is a premium technical partner for companies that need surgical speed, total precision, and scalable results in software development, MVPs, and digital transformation.";
  const keywords = propKeywords || "software development, mvp launch, web development, react native, next.js, elite tech studio, tech agency, acceleratebyte";
  const image = propImage || "/opengraph.jpg";
  const type = propType || "website";

  const siteTitle = title.includes("AccelerateByte") ? title : `${title} | AccelerateByte`;
  const domain = "https://acceleratebyte.com";
  const currentUrl = canonical || (url ? (url.startsWith('http') ? url : `${domain}${url}`) : `${domain}${location}`);

  return (
    <Helmet>
      {/* Standard Metadata */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="AccelerateByte" />

      {/* Organization — sitewide identity for AI/search */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://acceleratebyte.com/#organization",
          "name": "AccelerateByte",
          "url": "https://acceleratebyte.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://acceleratebyte.com/opengraph.jpg"
          },
          "description": "Elite tech studio based in Mumbai offering software development, MVP launch, mobile app development, and technical consulting for high-growth companies.",
          "foundingDate": "2025",
          "email": "hello@acceleratebyte.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Mumbai",
            "addressRegion": "Maharashtra",
            "addressCountry": "IN"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "hello@acceleratebyte.com",
            "contactType": "customer service",
            "areaServed": ["IN", "GB", "US", "AE"],
            "availableLanguage": "English"
          },
          "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "United Arab Emirates" }
          ],
          "knowsAbout": [
            "Software Development", "Web Development", "Mobile App Development",
            "MVP Launch", "React", "Next.js", "Node.js", "Technical Consulting"
          ],
          "sameAs": [
            "https://github.com/acceleratebyte",
            "https://linkedin.com/company/acceleratebyte"
          ]
        }).replace(/</g, "\\u003c")}
      </script>

      {/* WebSite — enables sitelinks in Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://acceleratebyte.com/#website",
          "name": "AccelerateByte",
          "url": "https://acceleratebyte.com",
          "publisher": { "@id": "https://acceleratebyte.com/#organization" }
        }).replace(/</g, "\\u003c")}
      </script>

      {/* WebPage — speakable markup for AI/voice surfaces */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${currentUrl}#webpage`,
          "url": currentUrl,
          "name": siteTitle,
          "description": description,
          "isPartOf": { "@id": "https://acceleratebyte.com/#website" },
          "about": { "@id": "https://acceleratebyte.com/#organization" },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "h2", "[data-speakable]"]
          }
        }).replace(/</g, "\\u003c")}
      </script>
    </Helmet>
  );
}
